import { useMemo } from 'react';
import { useSettings } from '@wix/tpa-settings/react';

import { useOnlineOrdersStorage } from '../storage';
import settingsParams from '../settingsParams';
import {
  parseMenuOrder,
  getOrderedMenus,
  applyMenuEditorSettings,
} from '../../../utils/menuEditorSettings';
import { buildItemAvailabilityByPlacementId } from '../../../utils/menuAvailability';
import type { ItemAvailabilityByPlacementId } from '../../../utils/menuAvailability';
import type { PopulatedMenu } from '../../../types/menu';

type UseProcessedMenusResult = {
  processedMenus: PopulatedMenu[];
  itemAvailabilityByPlacementId: ItemAvailabilityByPlacementId;
};

export const useProcessedMenus = (): UseProcessedMenusResult => {
  const {
    menus,
    currentOperation,
    itemMap,
    menuAvailabilityById,
    menuAvailabilityStatusById,
    menuNextAvailabilityById,
    sectionAvailabilityById,
    sectionAvailabilityStatusById,
    dispatchesInfo,
    selectedDispatchType,
    timezone,
  } = useOnlineOrdersStorage();
  const settings = useSettings();

  const sortedMenuIds = settings.get(settingsParams.sortedMenuIds);
  const showUnavailableOptions = settings.get(
    settingsParams.showUnavailableOptions,
  );
  const showExpired = settings.get(settingsParams.showExpired);
  const showUnavailableAndExpiredLast = settings.get(
    settingsParams.showUnavailableAndExpiredLast,
  );
  const selectedTimeSlot = selectedDispatchType
    ? dispatchesInfo[selectedDispatchType]?.selectedTimeSlot
    : undefined;

  const itemAvailabilityByPlacementId = useMemo(
    () =>
      buildItemAvailabilityByPlacementId({
        menus,
        itemMap,
        menuAvailabilityById,
        menuAvailabilityStatusById,
        sectionAvailabilityById,
        sectionAvailabilityStatusById,
        selectedTimeSlot,
        selectedDispatchType,
        timezone,
      }),
    [
      menus,
      itemMap,
      menuAvailabilityById,
      menuAvailabilityStatusById,
      sectionAvailabilityById,
      sectionAvailabilityStatusById,
      selectedTimeSlot,
      selectedDispatchType,
      timezone,
    ],
  );

  const processedMenus = useMemo(() => {
    const menuOrder = parseMenuOrder(sortedMenuIds);
    const ordered = getOrderedMenus(
      menus,
      menuOrder[currentOperation?.id ?? ''],
    );

    return applyMenuEditorSettings(ordered, itemMap, {
      showUnavailableOptions,
      showExpired,
      showUnavailableAndExpiredLast,
      menuAvailabilityById,
      menuAvailabilityStatusById,
      menuNextAvailabilityById,
      itemAvailabilityByPlacementId,
      sectionAvailabilityById,
      sectionAvailabilityStatusById,
      timezone,
    });
  }, [
    menus,
    currentOperation?.id,
    sortedMenuIds,
    itemMap,
    showUnavailableOptions,
    showExpired,
    showUnavailableAndExpiredLast,
    itemAvailabilityByPlacementId,
    menuAvailabilityById,
    menuAvailabilityStatusById,
    menuNextAvailabilityById,
    sectionAvailabilityById,
    sectionAvailabilityStatusById,
    timezone,
  ]);

  return { processedMenus, itemAvailabilityByPlacementId };
};
